import React, { useContext, useEffect, useMemo, useState } from "react";
import { BarChartItem, Day, HorizontalBarChart, LineChart, Serie, summary, WeekChart } from "react-charts";
import { groupBy, sum } from "simple-utils";
import { Dropdown, useWindowWidth } from "tooltip";
import { usePrivacyOn } from "../account/table/privacy-mode";
import { TypeSummary } from "../channels-page/channel-account-detail";
import { Loading } from "../components/Loading";
import { createContextProvider } from "../components/store-utils";
import { load } from "../loaders/basic-loader";
import { buildDailyReports } from "../loaders/report-entry-loaders";
import { bigNumber } from "../utils/money-utils";
import { PeriodLabel, SettingsButton } from "./ad-page-settings";
import { AdsCharts, AdsDailyReportCharts } from "./AdsCharts";
import { AdsDetailSummaryHeader } from "./AdsDetailSummaryHeader";
import { AdSummaryItemList } from "./AdSummary";
import { AdDetailContext, AdDetailStore, DetailState } from "./DetailStore";
import { DashError, DashErrorView } from "./ErrorPage";
import { AccountFunnel } from "./funnel";
import { DetailReport } from "./models";

function Header(){
    const {state} = useContext(AdDetailContext);
    const width = useWindowWidth();
    const privacy = usePrivacyOn();
    return <>
        <div className="d-flex mb-4 align-items-center">
        {state.report?.report.franchise == 1 && !privacy && <img src="royal-logo.png" className="me-4 royal-logo" style={{opacity:.8, maxWidth:'110px'}} />}
        <h2 className="flex-grow-1"><small className="opacity-75">Dashboard Leads</small> {state.report.report.name}</h2>
        {width > 800 && <SettingsButton />}
        </div>
        {width <= 800 && <div className="mb-4 text-center"><SettingsButton large /></div>}
    </>
}

export function TypeCounter({title, icon, className, value}:{title:string, icon:string, className:string, value:number}){
    return <div className={`type-counter type-counter-${className}`}>
        <i className={icon}></i>
        <span>
            <small>{title}</small>
            <big>{bigNumber(value)}</big>
        </span>
    </div>
}

async function loadReport(id:number, period = ''){
    return await load("api/v1/detail-report/" + id + (period ? `?period=${period}` : '')) as DetailReport
}

function DetailPage(){
    const [loading, setLoading] = useState(true);
    const {dispatch, state} = useContext(AdDetailContext);
    const [error, setError] = useState<DashError>(null)
    
    useEffect(()=>{
        setLoading(true);
        (async function (){try{
            const report = await loadReport(state.account, state.period);
            report.report.dailyReports = buildDailyReports(report.report);
            dispatch({type:"setReport", report})
            setLoading(false);
        }catch(e){
            console.log(e)
            setError(e);
        }})();

    }, [state.period]);

    if(error)
        return <DashErrorView error={error} />
        
    if(loading)
        return <div className="m-2"><Loading /></div>

    return <div className="m-2 m-md-3 m-lg-4">
            <Header />
            <PeriodLabel />
            <div style={{maxWidth: "1200px"}}>
                <AdsDetailSummaryHeader />
                <AccountFunnel account={state.report.report} />
                {state.report.report.franchise == 1 && <TypeSummary showOther={false} report={state.report.channel} numLeads={state.report.report.nLeads} />}
                <AdsCharts />
                <WeekHourCharts />
                <AdsDailyReportCharts />
                <AdSummaryItemList />
            </div>
        </div>
}

export function AdsDetailPage({account}:{account:number}){
    return <AdDetailStore initialValue={{account}}>
        <DetailPage />
    </AdDetailStore>
   
}

const types = {
    weekhour: {title: 'Dados de dia da semana e horário', label: 'Dia da Semana e Hora'},
    weekday: {title: 'Dados de dia da semana', label: 'Dia da Semana'},
    hour: {title: 'Dados por horário', label: 'Horário'}
} as const

type WeekChartModel =  keyof typeof types;

function HourChartMenu({model, setModel}:{model:WeekChartModel, setModel:(a:WeekChartModel)=>void}){
    return <Dropdown className="btn-primary ms-2" trigger={<i className="fa fa-ellipsis-vertical"></i>} placement="bottom-end">
        {Object.entries(types).map(([k, a]) =>  <li key={k}><a className={`dropdown-item ${model == k ? 'active' : ''}`} 
        onClick={() => setModel(k as WeekChartModel)} href="javascript:void(0)"
        >{a.label}</a></li>)}
    </Dropdown>
}

function WeekHourCharts(){
    const [model, setModel] = useState<WeekChartModel>("weekhour");


    return <div className='col-lg mt-3 mb-5'>
        <h4 className="d-flex"><span>{types[model].title}</span>
            <HourChartMenu model={model} setModel={setModel} />
        </h4>
        {model == 'weekhour' && <WeekHourChart />}
        {model == 'hour' && <OnlyHourChart />}
        {model == 'weekday' && <OnlyDayChart />}
    </div>
}

function WeekHourChart(){
    const {state} = useContext(AdDetailContext);
    if(!state.report.detail?.hourlyReport) return null
    return <WeekChart data={state.report.detail.hourlyReport} />
}

function OnlyHourChart(){
    const {state} = useContext(AdDetailContext);
    const data = useMemo(function():Serie[]{
        const x = state.report.detail?.hourlyReport
        if(!x) return null;

        const hours = summary(x, "hour") as [number, number][];
        return [{data: hours.map(h => ({x: h[0], y: h[1]})), label: "Hora", type:"simple-bar", style:"color1"}];

    }, [state.report.detail?.hourlyReport]);

    if(!data) return null
    return <LineChart data={data} height={300} hideLegend={true}
        xLabelFunction={(a) => `${Math.ceil(a)}h`}
         createTooltip={(e) => <><b>{e[0].item.x}h:</b> {e[0].item.y}</>} />
}

const weekdays = {
    sun: "domingo",
    mon: "segunda-feira",
    tue: "terça-feira",
    wed: "quarta-feira",
    thu: "quinta-feira",
    fri: "sexta-feira",
    sat: "sábado"
  }

function OnlyDayChart(){
    const {state} = useContext(AdDetailContext);
    const data = useMemo(function():BarChartItem[]{
        const x = state.report.detail?.hourlyReport
        if(!x) return null;

        const days = summary(x, "day") as [Day, number][];
        return Object.values(days).map(([label, value]) => ({label:weekdays[label], value, style:"color1"}));

    }, [state.report.detail?.hourlyReport]);

    if(!data) return null
    return <HorizontalBarChart data={data} tooltip={(a) => <><b>Leads:</b> {a.value}</>} />
}